import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Search, Globe, XCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Complete list of 121 supported countries from Guardarian
const SUPPORTED_COUNTRIES = {
  europe: {
    name: "Europe",
    emoji: "🇪🇺",
    onRampMethod: "SEPA Bank Transfer, Card",
    countries: [
      { name: "Austria", code: "AT", flag: "🇦🇹" },
      { name: "Belgium", code: "BE", flag: "🇧🇪" },
      { name: "Bosnia and Herzegovina", code: "BA", flag: "🇧🇦" },
      { name: "Bulgaria", code: "BG", flag: "🇧🇬" },
      { name: "Croatia", code: "HR", flag: "🇭🇷" },
      { name: "Cyprus", code: "CY", flag: "🇨🇾" },
      { name: "Czechia", code: "CZ", flag: "🇨🇿" },
      { name: "Denmark", code: "DK", flag: "🇩🇰" },
      { name: "Estonia", code: "EE", flag: "🇪🇪" },
      { name: "Finland", code: "FI", flag: "🇫🇮" },
      { name: "France", code: "FR", flag: "🇫🇷" },
      { name: "Germany", code: "DE", flag: "🇩🇪" },
      { name: "Greece", code: "GR", flag: "🇬🇷" },
      { name: "Hungary", code: "HU", flag: "🇭🇺" },
      { name: "Iceland", code: "IS", flag: "🇮🇸" },
      { name: "Ireland", code: "IE", flag: "🇮🇪" },
      { name: "Italy", code: "IT", flag: "🇮🇹" },
      { name: "Latvia", code: "LV", flag: "🇱🇻" },
      { name: "Liechtenstein", code: "LI", flag: "🇱🇮" },
      { name: "Lithuania", code: "LT", flag: "🇱🇹" },
      { name: "Luxembourg", code: "LU", flag: "🇱🇺" },
      { name: "Malta", code: "MT", flag: "🇲🇹" },
      { name: "Moldova", code: "MD", flag: "🇲🇩" },
      { name: "Montenegro", code: "ME", flag: "🇲🇪" },
      { name: "Netherlands", code: "NL", flag: "🇳🇱" },
      { name: "Norway", code: "NO", flag: "🇳🇴" },
      { name: "Poland", code: "PL", flag: "🇵🇱" },
      { name: "Portugal", code: "PT", flag: "🇵🇹" },
      { name: "Romania", code: "RO", flag: "🇷🇴" },
      { name: "San Marino", code: "SM", flag: "🇸🇲" },
      { name: "Serbia", code: "RS", flag: "🇷🇸" },
      { name: "Slovakia", code: "SK", flag: "🇸🇰" },
      { name: "Slovenia", code: "SI", flag: "🇸🇮" },
      { name: "Spain", code: "ES", flag: "🇪🇸" },
      { name: "Sweden", code: "SE", flag: "🇸🇪" },
      { name: "Switzerland", code: "CH", flag: "🇨🇭" },
      { name: "Ukraine", code: "UA", flag: "🇺🇦" },
      { name: "United Kingdom", code: "GB", flag: "🇬🇧" }
    ]
  },
  americas: {
    name: "Americas",
    emoji: "🌎",
    onRampMethod: "Card, Bank Transfer",
    countries: [
      { name: "Argentina", code: "AR", flag: "🇦🇷" },
      { name: "Brazil", code: "BR", flag: "🇧🇷" },
      { name: "Chile", code: "CL", flag: "🇨🇱" },
      { name: "Colombia", code: "CO", flag: "🇨🇴" },
      { name: "Costa Rica", code: "CR", flag: "🇨🇷" },
      { name: "Dominica", code: "DM", flag: "🇩🇲" },
      { name: "Dominican Republic", code: "DO", flag: "🇩🇴" },
      { name: "Ecuador", code: "EC", flag: "🇪🇨" },
      { name: "El Salvador", code: "SV", flag: "🇸🇻" },
      { name: "Grenada", code: "GD", flag: "🇬🇩" },
      { name: "Guatemala", code: "GT", flag: "🇬🇹" },
      { name: "Guyana", code: "GY", flag: "🇬🇾" },
      { name: "Honduras", code: "HN", flag: "🇭🇳" },
      { name: "Mexico", code: "MX", flag: "🇲🇽" },
      { name: "Paraguay", code: "PY", flag: "🇵🇾" },
      { name: "Peru", code: "PE", flag: "🇵🇪" },
      { name: "Saint Lucia", code: "LC", flag: "🇱🇨" },
      { name: "Saint Vincent", code: "VC", flag: "🇻🇨" },
      { name: "Suriname", code: "SR", flag: "🇸🇷" },
      { name: "United States", code: "US", flag: "🇺🇸" },
      { name: "Uruguay", code: "UY", flag: "🇺🇾" }
    ]
  },
  asiaPacific: {
    name: "Asia & Pacific",
    emoji: "🌏",
    onRampMethod: "Card, Bank Transfer",
    countries: [
      { name: "Armenia", code: "AM", flag: "🇦🇲" },
      { name: "Australia", code: "AU", flag: "🇦🇺" },
      { name: "Azerbaijan", code: "AZ", flag: "🇦🇿" },
      { name: "Bhutan", code: "BT", flag: "🇧🇹" },
      { name: "Brunei", code: "BN", flag: "🇧🇳" },
      { name: "Fiji", code: "FJ", flag: "🇫🇯" },
      { name: "Georgia", code: "GE", flag: "🇬🇪" },
      { name: "Hong Kong", code: "HK", flag: "🇭🇰" },
      { name: "India", code: "IN", flag: "🇮🇳" },
      { name: "Indonesia", code: "ID", flag: "🇮🇩" },
      { name: "Japan", code: "JP", flag: "🇯🇵" },
      { name: "Kazakhstan", code: "KZ", flag: "🇰🇿" },
      { name: "Kiribati", code: "KI", flag: "🇰🇮" },
      { name: "Kyrgyzstan", code: "KG", flag: "🇰🇬" },
      { name: "Laos", code: "LA", flag: "🇱🇦" },
      { name: "Maldives", code: "MV", flag: "🇲🇻" },
      { name: "Marshall Islands", code: "MH", flag: "🇲🇭" },
      { name: "Micronesia", code: "FM", flag: "🇫🇲" },
      { name: "Nauru", code: "NR", flag: "🇳🇷" },
      { name: "New Zealand", code: "NZ", flag: "🇳🇿" },
      { name: "Pakistan", code: "PK", flag: "🇵🇰" },
      { name: "Papua New Guinea", code: "PG", flag: "🇵🇬" },
      { name: "Samoa", code: "WS", flag: "🇼🇸" },
      { name: "Singapore", code: "SG", flag: "🇸🇬" },
      { name: "Solomon Islands", code: "SB", flag: "🇸🇧" },
      { name: "Sri Lanka", code: "LK", flag: "🇱🇰" },
      { name: "Tajikistan", code: "TJ", flag: "🇹🇯" },
      { name: "Thailand", code: "TH", flag: "🇹🇭" },
      { name: "Timor-Leste", code: "TL", flag: "🇹🇱" },
      { name: "Tonga", code: "TO", flag: "🇹🇴" },
      { name: "Turkey", code: "TR", flag: "🇹🇷" },
      { name: "Turkmenistan", code: "TM", flag: "🇹🇲" },
      { name: "Tuvalu", code: "TV", flag: "🇹🇻" },
      { name: "Uzbekistan", code: "UZ", flag: "🇺🇿" }
    ]
  },
  africa: {
    name: "Africa",
    emoji: "🌍",
    onRampMethod: "Card, Bank Transfer",
    countries: [
      { name: "Benin", code: "BJ", flag: "🇧🇯" },
      { name: "Cabo Verde", code: "CV", flag: "🇨🇻" },
      { name: "Central African Republic", code: "CF", flag: "🇨🇫" },
      { name: "Chad", code: "TD", flag: "🇹🇩" },
      { name: "Comoros", code: "KM", flag: "🇰🇲" },
      { name: "Congo", code: "CG", flag: "🇨🇬" },
      { name: "Djibouti", code: "DJ", flag: "🇩🇯" },
      { name: "Equatorial Guinea", code: "GQ", flag: "🇬🇶" },
      { name: "Eswatini", code: "SZ", flag: "🇸🇿" },
      { name: "Gabon", code: "GA", flag: "🇬🇦" },
      { name: "Gambia", code: "GM", flag: "🇬🇲" },
      { name: "Ghana", code: "GH", flag: "🇬🇭" },
      { name: "Guinea", code: "GN", flag: "🇬🇳" },
      { name: "Guinea-Bissau", code: "GW", flag: "🇬🇼" },
      { name: "Lesotho", code: "LS", flag: "🇱🇸" },
      { name: "Liberia", code: "LR", flag: "🇱🇷" },
      { name: "Madagascar", code: "MG", flag: "🇲🇬" },
      { name: "Mauritania", code: "MR", flag: "🇲🇷" },
      { name: "Niger", code: "NE", flag: "🇳🇪" },
      { name: "Rwanda", code: "RW", flag: "🇷🇼" },
      { name: "Sao Tome and Principe", code: "ST", flag: "🇸🇹" },
      { name: "Seychelles", code: "SC", flag: "🇸🇨" },
      { name: "Sierra Leone", code: "SL", flag: "🇸🇱" },
      { name: "Togo", code: "TG", flag: "🇹🇬" },
      { name: "Tunisia", code: "TN", flag: "🇹🇳" },
      { name: "Zambia", code: "ZM", flag: "🇿🇲" }
    ]
  },
  middleEast: {
    name: "Middle East",
    emoji: "🏜️",
    onRampMethod: "Card, Bank Transfer",
    countries: [
      { name: "Kuwait", code: "KW", flag: "🇰🇼" },
      { name: "Oman", code: "OM", flag: "🇴🇲" }
    ]
  }
};

// Countries NOT supported
const UNSUPPORTED_COUNTRIES = [
  { name: "Canada", code: "CA", flag: "🇨🇦" },
  { name: "Egypt", code: "EG", flag: "🇪🇬" },
  { name: "Morocco", code: "MA", flag: "🇲🇦" },
  { name: "Nigeria", code: "NG", flag: "🇳🇬" },
  { name: "Philippines", code: "PH", flag: "🇵🇭" },
  { name: "Saudi Arabia", code: "SA", flag: "🇸🇦" },
  { name: "United Arab Emirates", code: "AE", flag: "🇦🇪" }
];

export default function SupportedCountriesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');

  const totalCountries = useMemo(() => {
    return Object.values(SUPPORTED_COUNTRIES).reduce(
      (sum, region) => sum + region.countries.length, 0
    );
  }, []);

  const filteredRegions = useMemo(() => {
    const search = searchTerm.toLowerCase();
    
    return Object.entries(SUPPORTED_COUNTRIES)
      .map(([key, region]) => ({
        ...region,
        key,
        countries: region.countries.filter(country => 
          country.name.toLowerCase().includes(search)
        )
      }))
      .filter(region => 
        (selectedRegion === 'all' || region.key === selectedRegion) &&
        region.countries.length > 0
      );
  }, [searchTerm, selectedRegion]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
              <Globe className="w-8 h-8 text-emerald-600" />
              Supported Countries
            </h1>
            <p className="text-gray-600">
              Send money to <span className="font-semibold text-emerald-600">{totalCountries} countries</span> worldwide
            </p>
          </div>

          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={selectedRegion === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedRegion('all')}
                  >
                    All
                  </Button>
                  {Object.entries(SUPPORTED_COUNTRIES).map(([key, region]) => (
                    <Button
                      key={key}
                      variant={selectedRegion === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedRegion(key)}
                    >
                      {region.emoji} {region.name}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {filteredRegions.map((region) => (
            <Card key={region.key} className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">{region.emoji}</span>
                  {region.name}
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({region.countries.length} countries)
                  </span>
                </CardTitle>
                <p className="text-sm text-gray-500">
                  On-ramp: <span className="font-medium">{region.onRampMethod}</span>
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {region.countries.map((country) => (
                    <div
                      key={country.code}
                      className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 hover:bg-emerald-50 transition-colors"
                    >
                      <span className="text-xl">{country.flag}</span>
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {country.name}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                Not Currently Supported
              </CardTitle>
              <p className="text-sm text-red-600">
                These countries are not available due to regulatory restrictions
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {UNSUPPORTED_COUNTRIES.map((country) => (
                  <div
                    key={country.code}
                    className="flex items-center gap-2 p-2 rounded-lg bg-red-100"
                  >
                    <span className="text-xl opacity-50">{country.flag}</span>
                    <span className="text-sm text-red-700 truncate">
                      {country.name}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              Coverage is expanding! We're constantly adding new countries.
            </p>
            <p className="mt-1">
              Powered by <a href="https://guardarian.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Guardarian</a> payment infrastructure
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
